import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import useSWR from 'swr'

import NavBar from '../landing/navbar'
import Footer from '../landing/footer'
import { useAuth } from '../../context/auth'
import { getStripePrices, createStripeCheckoutSessionGuest } from '../../api'
import {
  FREE_PRICING_ITEMS,
  PRO_PRICING_ITEMS,
} from './pricingConstants'
import { renderPricingItem } from './pricingItem'
import '../get-premium/styles.scss'
import './pricing.scss'

type PriceInfo = { priceId: string; unitAmount: number; currency: string; interval: string } | null

const formatPrice = (price: PriceInfo, locale: string): string => {
  if (!price || price.unitAmount == null) return ''
  const value = price.unitAmount / 100
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: price.currency.toUpperCase(),
  }).format(value)
}

const getYearlySavingsPercent = (monthly: PriceInfo, yearly: PriceInfo): number | null => {
  if (!monthly?.unitAmount || !yearly?.unitAmount || monthly.unitAmount <= 0) return null
  const costIfMonthly12 = 12 * monthly.unitAmount
  const costYearly = yearly.unitAmount
  if (costYearly >= costIfMonthly12) return null
  return Math.round(((costIfMonthly12 - costYearly) / costIfMonthly12) * 100)
}

const PricingPage = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState<'monthly' | 'yearly' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const { data: prices } = useSWR('/api/stripe/prices', getStripePrices)
  const locale = typeof navigator !== 'undefined' ? navigator.language : 'en'
  const monthlyFormatted = formatPrice(prices?.monthly ?? null, locale)
  const yearlyFormatted = formatPrice(prices?.yearly ?? null, locale)
  const yearlySavingsPercent = getYearlySavingsPercent(prices?.monthly ?? null, prices?.yearly ?? null)
  const monthlyPriceId = prices?.monthly?.priceId
  const yearlyPriceId = prices?.yearly?.priceId

  const handleGuestCheckout = async (priceId: string | undefined, plan: 'monthly' | 'yearly') => {
    if (!priceId) {
      setError('This plan is not configured. Please try again later.')
      return
    }
    setError(null)
    setLoading(plan)
    try {
      const res = await createStripeCheckoutSessionGuest(priceId)
      if (res?.url) window.location.href = res.url
      else {
        setError('Something went wrong. Please try again.')
        setLoading(null)
      }
    } catch {
      setError('Something went wrong. Please try again.')
      setLoading(null)
    }
  }

  return (
    <div className='pricing-page'>
      <NavBar />
      <main className='pricing-main'>
        <div className='get-premium-page'>
          <div className='get-premium-container'>
            <div className='get-premium-header'>
              <h1>Pricing</h1>
              <p className='lead'>
                Simple plans. No hidden fees.<br />Pick what fits your business.
              </p>
            </div>

            <div className='get-premium-cards'>
              <div className='premium-card'>
                <h2>Free</h2>
                <p className='premium-desc'>No charge. Everything you need to try TinyInvoice.app with your clients.</p>
                <ul className='pricing-items-list'>
                  {FREE_PRICING_ITEMS.map((item, i) => renderPricingItem(item, i))}
                </ul>
                <Link to='/login' className='button btn btn-light w-100 text-center pricing-card-cta'>
                  Get started
                </Link>
              </div>
              <div className='premium-card premium-card-featured'>
                <h2>Premium</h2>
                <p className='premium-desc'>Unlimited invoices and full access. <br />Cancel anytime.</p>
                <ul className='pricing-items-list'>
                  {PRO_PRICING_ITEMS.map((item, i) => renderPricingItem(item, i))}
                </ul>
                {user ? (
                  <Link to='/get-premium' className='button btn btn-primary w-100 text-center pricing-card-cta'>
                    Get Premium
                  </Link>
                ) : (
                  <div className='pricing-pro-ctas'>
                    <button
                      type='button'
                      className='button btn btn-light w-100 pricing-plan-btn'
                      disabled={!!loading || !monthlyPriceId}
                      onClick={() => handleGuestCheckout(monthlyPriceId, 'monthly')}
                    >
                      {loading === 'monthly' ? (
                        'Taking you to checkout…'
                      ) : (
                        <span className='pricing-plan-btn__label'>
                          {monthlyFormatted ? (
                            <span className='pricing-plan-btn__row'>
                              <span className='pricing-plan-btn__amount'>{monthlyFormatted}</span>
                              <span className='pricing-plan-btn__interval'> / monthly</span>
                            </span>
                          ) : (
                            'Choose monthly'
                          )}
                        </span>
                      )}
                    </button>
                    <button
                      type='button'
                      className='button btn btn-primary w-100 pricing-plan-btn pricing-plan-btn--yearly'
                      disabled={!!loading || !yearlyPriceId}
                      onClick={() => handleGuestCheckout(yearlyPriceId, 'yearly')}
                    >
                      {loading === 'yearly' ? (
                        'Taking you to checkout…'
                      ) : (
                        <span className='pricing-plan-btn__label'>
                          {yearlyFormatted ? (
                            <span className='pricing-plan-btn__row'>
                              <span className='pricing-plan-btn__amount'>{yearlyFormatted}</span>
                              <span className='pricing-plan-btn__interval'> / yearly</span>
                            </span>
                          ) : (
                            'Choose yearly'
                          )}
                          {yearlySavingsPercent != null && yearlySavingsPercent > 0 && (
                            <span className='pricing-plan-btn__save'>Save {yearlySavingsPercent}%</span>
                          )}
                        </span>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div className='alert alert-danger mt-3' role='alert'>
                {error}
              </div>
            )}

            <p className='get-premium-footer'>
              Secure payment by Stripe. You can update your card or cancel your subscription anytime from your account.
            </p>

            <Link to='/' className='back-link'>
              ← Back to home
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default PricingPage
